import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    Bell,
    FolderOpen,
    User,
    Award,
    Building2,
    FileText,
} from 'lucide-react';
import { staffClient } from 'clients/staff';
import { articleApi } from 'apis/article';
import Loading from 'ui/Loading';
import PageContainer from 'ui/PageContainer';
import PageHeader from 'ui/PageHeader';
import { Badge } from 'ui/shadcn';
import { cn } from 'lib/utils';

interface StaffStats {
    electricPE: number;
    firePE: number;
    telecomPE: number;
    electricEng: number;
    fireEng: number;
    telecomEng: number;
}

interface Notice {
    id: number;
    title: string;
    created_at: string;
}

const isNewArticle = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
};

const SECTION_CARD = 'rounded border border-border bg-card overflow-hidden';
const SECTION_HEADER =
    'flex items-center justify-between gap-2 px-4 h-11 border-b border-border';
const SECTION_TITLE = 'text-md font-semibold text-foreground';
const SECTION_BODY = 'p-4';

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    const [siteStats, setSiteStats] = useState({
        inProgress: 0,
        contractCompleted: 0,
        completed: 0,
    });
    const [sites, setSites] = useState<any[]>([]);
    const [expandedSiteType, setExpandedSiteType] = useState<string | null>(null);

    const [staffStats, setStaffStats] = useState<StaffStats>({
        electricPE: 0,
        firePE: 0,
        telecomPE: 0,
        electricEng: 0,
        fireEng: 0,
        telecomEng: 0,
    });

    const [notices, setNotices] = useState<Notice[]>([]);
    const [noticeBoardId, setNoticeBoardId] = useState<number | null>(null);
    const [generalBoards, setGeneralBoards] = useState<any[]>([]);

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            await Promise.all([
                fetchSiteStats(),
                fetchStaffStats(),
                fetchNotices(),
                fetchGeneralBoards(),
            ]);
        } catch (error) {
            console.error('[Home] Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSiteStats = async () => {
        try {
            const [siteResponse, myInfoResponse] = await Promise.all([
                staffClient.get('/site'),
                staffClient.get('/auth/my-info'),
            ]);
            let siteList = Array.isArray(siteResponse.data)
                ? siteResponse.data
                : siteResponse.data?.data && Array.isArray(siteResponse.data.data)
                  ? siteResponse.data.data
                  : [];
            const type = myInfoResponse.data?.token?.type;
            if (type === 'STAFF') {
                const mySiteIds: number[] = await staffClient
                    .get('/auth/my-sites')
                    .then((r) => r.data);
                siteList = siteList.filter((s: any) => mySiteIds.includes(s.id));
            }
            setSites(siteList);
            setSiteStats({
                inProgress: siteList.filter((s: any) => s.type === 'IN_PROGRESS').length,
                contractCompleted: siteList.filter(
                    (s: any) => s.type === 'CONTRACT_COMPLETED'
                ).length,
                completed: siteList.filter((s: any) => s.type === 'COMPLETED').length,
            });
        } catch (error) {
            console.error('[Home] fetchSiteStats error:', error);
        }
    };

    const fetchStaffStats = async () => {
        try {
            const response = await staffClient.get('/staffs', { params: { limit: 1000 } });
            const staffs = response.data?.data || [];
            const stats: StaffStats = {
                electricPE: 0,
                firePE: 0,
                telecomPE: 0,
                electricEng: 0,
                fireEng: 0,
                telecomEng: 0,
            };
            staffs.forEach((staff: any) => {
                if (staff.qualifications && Array.isArray(staff.qualifications)) {
                    staff.qualifications.forEach((qual: any) => {
                        const q = qual.technical_qualification || '';
                        if (q === '전기기술사') stats.electricPE++;
                        if (q === '소방기술사') stats.firePE++;
                        if (q === '정보통신기술사') stats.telecomPE++;
                        if (q === '전기기사') stats.electricEng++;
                        if (q === '소방설비기사(기계)' || q === '소방설비기사(전기)') {
                            stats.fireEng++;
                        }
                        if (q === '정보통신기사') stats.telecomEng++;
                    });
                }
            });
            setStaffStats(stats);
        } catch (error) {
            console.error('[Home] fetchStaffStats error:', error);
        }
    };

    const fetchNotices = async () => {
        try {
            const response = await staffClient.get('/board/notice');
            if (response.data?.id) {
                setNoticeBoardId(response.data.id);
                const noticeResponse = await articleApi.getArticles({
                    boardId: response.data.id,
                    limit: 8,
                });
                const articles = noticeResponse.data?.data || noticeResponse.data || [];
                setNotices(
                    articles.slice(0, 8).map((article: any) => ({
                        id: article.id,
                        title: article.title,
                        created_at: article.created_at,
                    }))
                );
            }
        } catch (error) {
            console.error('[Home] fetchNotices error:', error);
        }
    };

    const fetchGeneralBoards = async () => {
        try {
            const ret = await staffClient.get('/board/general', { params: { limit: 1000 } });
            const boardData = ret?.data?.data?.data || ret?.data?.data || ret?.data || [];
            const publicBoards = boardData
                .filter((board: any) => board.visible === true)
                .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
            setGeneralBoards(publicBoards);
        } catch (error) {
            console.error('[Home] fetchGeneralBoards error:', error);
        }
    };

    const handleNoticeClick = (noticeId: number) => {
        if (noticeBoardId) {
            navigate(`/boards/${noticeBoardId}/articles/${noticeId}`);
        } else {
            navigate('/notice');
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Loading text="대시보드를 불러오는 중..." />
            </PageContainer>
        );
    }

    const totalSites =
        siteStats.inProgress + siteStats.contractCompleted + siteStats.completed;

    const peCells: Array<{
        label: string;
        count: number;
        filter: string;
    }> = [
        { label: '전기기술사', count: staffStats.electricPE, filter: '전기기술사' },
        { label: '소방기술사', count: staffStats.firePE, filter: '소방기술사' },
        { label: '정보통신기술사', count: staffStats.telecomPE, filter: '정보통신기술사' },
    ];

    const engCells: Array<{
        label: string;
        count: number;
        filter: string;
    }> = [
        { label: '전기기사', count: staffStats.electricEng, filter: '전기기사' },
        { label: '소방설비기사', count: staffStats.fireEng, filter: '소방설비기사' },
        { label: '정보통신기사', count: staffStats.telecomEng, filter: '정보통신기사' },
    ];

    const workTypeBadges: Record<
        string,
        { label: string; tone: 'warning' | 'danger' | 'info' | 'success' }
    > = {
        ELECTRIC: { label: '전기', tone: 'warning' },
        FIRE: { label: '소방', tone: 'danger' },
        TELECOM: { label: '통신', tone: 'info' },
        NETWORK: { label: '통신', tone: 'info' },
    };

    return (
        <PageContainer>
            <PageHeader
                title="대시보드"
                subtitle="전체 현황을 한눈에 확인합니다"
            />

            {/* 바로가기 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <ShortcutCard
                    icon={<FolderOpen className="h-4 w-4" />}
                    title="일반게시판"
                >
                    <div className="flex flex-wrap gap-1.5">
                        {generalBoards.length > 0 ? (
                            generalBoards.map((board: any) => (
                                <button
                                    key={board.id}
                                    onClick={() => navigate(`/board/${board.id}`)}
                                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded border border-border bg-background text-sm text-neutral-700 hover:bg-accent hover:text-primary hover:border-navy-100 transition-colors duration-fast"
                                >
                                    <FileText className="h-3.5 w-3.5 text-neutral-400" />
                                    {board.name}
                                </button>
                            ))
                        ) : (
                            <span className="text-sm text-muted-foreground">
                                게시판 정보가 없습니다.
                            </span>
                        )}
                    </div>
                </ShortcutCard>

                <ShortcutCard
                    icon={<User className="h-4 w-4" />}
                    title="계정"
                >
                    <button
                        onClick={() => navigate('/account/mypage')}
                        className="inline-flex items-center gap-1.5 px-3 h-8 rounded border border-border bg-background text-sm text-neutral-700 hover:bg-accent hover:text-primary hover:border-navy-100 transition-colors duration-fast"
                    >
                        <User className="h-3.5 w-3.5 text-neutral-400" />
                        마이페이지
                    </button>
                </ShortcutCard>
            </div>

            {/* 메인 그리드 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* 좌측: 공지사항 */}
                <div className={SECTION_CARD}>
                    <button
                        type="button"
                        className={cn(
                            SECTION_HEADER,
                            'w-full hover:bg-muted transition-colors duration-fast cursor-pointer'
                        )}
                        onClick={() => navigate('/notice')}
                    >
                        <div className="flex items-center gap-2">
                            <Bell className="h-4 w-4 text-neutral-500" />
                            <h3 className={SECTION_TITLE}>공지사항</h3>
                        </div>
                        <ArrowRight className="h-4 w-4 text-neutral-400" />
                    </button>
                    <div>
                        {notices.length > 0 ? (
                            notices.map((notice, idx) => (
                                <button
                                    key={notice.id}
                                    onClick={() => handleNoticeClick(notice.id)}
                                    className={cn(
                                        'w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-muted transition-colors duration-fast text-left',
                                        idx < notices.length - 1 && 'border-b border-border'
                                    )}
                                >
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="text-base text-foreground truncate">
                                            {notice.title}
                                        </span>
                                        {isNewArticle(notice.created_at) && (
                                            <span className="shrink-0 inline-flex items-center justify-center h-4 px-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded leading-none">
                                                N
                                            </span>
                                        )}
                                    </div>
                                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                                        {new Date(notice.created_at).toLocaleDateString('ko-KR')}
                                    </span>
                                </button>
                            ))
                        ) : (
                            <div className="text-center text-sm text-muted-foreground py-12">
                                공지사항이 없습니다.
                            </div>
                        )}
                    </div>
                </div>

                {/* 우측: 현장 현황 + 기술사 보유현황 */}
                <div className="flex flex-col gap-4">
                    {/* 현장 현황 */}
                    <div className={SECTION_CARD}>
                        <button
                            type="button"
                            className={cn(
                                SECTION_HEADER,
                                'w-full hover:bg-muted transition-colors duration-fast cursor-pointer'
                            )}
                            onClick={() => navigate('/sites/list')}
                        >
                            <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-neutral-500" />
                                <h3 className={SECTION_TITLE}>현장 현황</h3>
                                <Badge variant="default" className="tabular-nums">
                                    {totalSites}개
                                </Badge>
                            </div>
                            <ArrowRight className="h-4 w-4 text-neutral-400" />
                        </button>
                        <div className={SECTION_BODY}>
                            <div className="grid grid-cols-3 gap-2">
                                <SiteTypeButton
                                    label="진행중"
                                    count={siteStats.inProgress}
                                    active={expandedSiteType === 'IN_PROGRESS'}
                                    onClick={() =>
                                        setExpandedSiteType(
                                            expandedSiteType === 'IN_PROGRESS' ? null : 'IN_PROGRESS'
                                        )
                                    }
                                />
                                <SiteTypeButton
                                    label="계약완료"
                                    count={siteStats.contractCompleted}
                                    active={expandedSiteType === 'CONTRACT_COMPLETED'}
                                    onClick={() =>
                                        setExpandedSiteType(
                                            expandedSiteType === 'CONTRACT_COMPLETED'
                                                ? null
                                                : 'CONTRACT_COMPLETED'
                                        )
                                    }
                                />
                                <SiteTypeButton
                                    label="준공"
                                    count={siteStats.completed}
                                    active={expandedSiteType === 'COMPLETED'}
                                    onClick={() =>
                                        setExpandedSiteType(
                                            expandedSiteType === 'COMPLETED' ? null : 'COMPLETED'
                                        )
                                    }
                                />
                            </div>

                            {expandedSiteType &&
                                sites.filter((s) => s.type === expandedSiteType).length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-border flex flex-col gap-1">
                                        {sites
                                            .filter((s) => s.type === expandedSiteType)
                                            .map((site: any) => (
                                                <button
                                                    key={site.id}
                                                    onClick={() =>
                                                        navigate(`/sites/${site.siteCode || site.id}`)
                                                    }
                                                    className="flex items-center justify-between gap-2 px-3 h-9 rounded text-base text-foreground hover:bg-muted transition-colors duration-fast text-left"
                                                >
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        <span className="font-medium truncate">
                                                            {site.name}
                                                        </span>
                                                        {site.workTypes && site.workTypes.length > 0 && (
                                                            <div className="flex gap-1 shrink-0">
                                                                {site.workTypes.map((wt: string) => {
                                                                    const badge = workTypeBadges[wt];
                                                                    if (!badge) return null;
                                                                    return (
                                                                        <Badge
                                                                            key={wt}
                                                                            variant={badge.tone}
                                                                            className="text-[10px] px-1.5 py-0"
                                                                        >
                                                                            {badge.label}
                                                                        </Badge>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <ArrowRight className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                                                </button>
                                            ))}
                                    </div>
                                )}
                        </div>
                    </div>

                    {/* 기술사/기사 보유현황 */}
                    <div className={SECTION_CARD}>
                        <button
                            type="button"
                            className={cn(
                                SECTION_HEADER,
                                'w-full hover:bg-muted transition-colors duration-fast cursor-pointer'
                            )}
                            onClick={() => navigate('/admin/qualifications')}
                        >
                            <div className="flex items-center gap-2">
                                <Award className="h-4 w-4 text-neutral-500" />
                                <h3 className={SECTION_TITLE}>자격 보유 현황</h3>
                            </div>
                            <ArrowRight className="h-4 w-4 text-neutral-400" />
                        </button>
                        <div className={SECTION_BODY}>
                            <QualBlock title="기술사" cells={peCells} navigate={navigate} />
                            <div className="mt-3 pt-3 border-t border-border">
                                <QualBlock title="기사" cells={engCells} navigate={navigate} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PageContainer>
    );
};

const ShortcutCard: React.FC<{
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, icon, children }) => (
    <div className={SECTION_CARD}>
        <div className={SECTION_HEADER}>
            <div className="flex items-center gap-2 text-neutral-500">
                {icon}
                <h3 className={SECTION_TITLE}>{title}</h3>
            </div>
        </div>
        <div className={SECTION_BODY}>{children}</div>
    </div>
);

const SiteTypeButton: React.FC<{
    label: string;
    count: number;
    active: boolean;
    onClick: () => void;
}> = ({ label, count, active, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            'flex flex-col items-center gap-1 px-3 py-2.5 rounded border transition-colors duration-fast',
            active
                ? 'bg-navy-50 border-navy-100 text-primary'
                : 'bg-neutral-50 border-transparent text-neutral-700 hover:bg-muted'
        )}
    >
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-xl font-semibold tabular-nums leading-tight">{count}</span>
    </button>
);

const QualBlock: React.FC<{
    title: string;
    cells: Array<{ label: string; count: number; filter: string }>;
    navigate: (to: string) => void;
}> = ({ title, cells, navigate }) => (
    <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
            {title}
        </h4>
        <div className="grid grid-cols-3 gap-2">
            {cells.map((cell) => (
                <button
                    key={cell.label}
                    onClick={() =>
                        navigate(`/admin/qualifications?filter=${encodeURIComponent(cell.filter)}`)
                    }
                    className="flex items-center justify-between gap-2 px-3 h-9 rounded border border-border bg-background hover:bg-muted transition-colors duration-fast"
                >
                    <span className="text-base text-neutral-700 truncate">{cell.label}</span>
                    <span className="text-base font-semibold tabular-nums text-foreground shrink-0">
                        {cell.count}
                        <span className="text-xs font-normal text-muted-foreground ml-0.5">명</span>
                    </span>
                </button>
            ))}
        </div>
    </div>
);

export default HomePage;
